import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { Button, Modal, ModalBody } from "reactstrap";

import ConfirmModal from "../../../Components/Common/ConfirmModal";
import {
    createMenuOptionValue,
    deleteMenuOptionValue,
    getMenuOption,
    getMenuOptionValues,
    updateMenuOptionValue,
} from "../../../helpers/backend_helper";
import { showAlertWithTimeout } from "../../../slices/Alert/reducer";
import { AppDispatch } from "../../../store";
import HeaderSidebarToggle from "../../../Components/Common/HeaderSidebarToggle";

const extractOptionValues = (payload: any): any[] => {
    if (!payload) {
        return [];
    }

    const data = payload?.data ?? payload;
    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.data)) {
        return data.data;
    }

    if (Array.isArray(data?.option_values)) {
        return data.option_values;
    }

    return [];
};

const getOptionValueId = (value: any): string | number | undefined => {
    if (!value || typeof value !== "object") {
        return undefined;
    }

    const candidateKeys = ["id", "value_id", "option_value_id", "optionValueId"];

    for (const key of candidateKeys) {
        const raw = (value as Record<string, any>)[key];
        if (raw !== undefined && raw !== null && raw !== "") {
            return raw as string | number;
        }
    }

    return undefined;
};

const getOptionValueLabel = (value: any): string => {
    if (!value || typeof value !== "object") {
        return "";
    }

    const candidateKeys = ["label", "name", "title", "displayName"];

    for (const key of candidateKeys) {
        const raw = (value as Record<string, any>)[key];
        if (typeof raw === "string" && raw.trim().length > 0) {
            return raw.trim();
        }
    }

    return "";
};

const getOptionValueAvailability = (value: any): boolean => {
    if (!value || typeof value !== "object") {
        return true;
    }

    const candidateKeys = ["is_available", "isAvailable", "available", "status", "state"];

    for (const key of candidateKeys) {
        const raw = (value as Record<string, any>)[key];
        if (typeof raw === "boolean") {
            return raw;
        }
        if (typeof raw === "number") {
            return raw !== 0;
        }
        if (typeof raw === "string") {
            const normalized = raw.trim().toLowerCase();
            if (["true", "1", "available", "active", "enabled"].includes(normalized)) {
                return true;
            }
            if (["false", "0", "inactive", "disabled", "unavailable"].includes(normalized)) {
                return false;
            }
        }
    }

    return true;
};

const MenuOptionValuesPage: React.FC = () => {
    const { optionId } = useParams();
    const dispatch = useDispatch<AppDispatch>();

    const [optionDetail, setOptionDetail] = useState<any>(null);
    const [values, setValues] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showFormModal, setShowFormModal] = useState(false);
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [formLabel, setFormLabel] = useState("");
    const [formAvailability, setFormAvailability] = useState(true);
    const [formError, setFormError] = useState("");
    const [editingValueId, setEditingValueId] = useState<string | number | null>(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState<string | number | null>(null);
    const [deleteTargetLabel, setDeleteTargetLabel] = useState("");

    const optionName = useMemo(() => {
        const name = optionDetail?.name ?? optionDetail?.label ?? optionDetail?.title;
        if (typeof name === "string" && name.trim().length > 0) {
            return name.trim();
        }
        return "Tùy chọn";
    }, [optionDetail]);

    const fetchValues = useCallback(async () => {
        if (!optionId) {
            setError("Không xác định được tùy chọn.");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const [optionResponse, valueResponse] = await Promise.all([
                getMenuOption(optionId),
                getMenuOptionValues(optionId),
            ]);

            setOptionDetail(optionResponse?.data?.data ?? optionResponse?.data ?? null);
            const list = extractOptionValues(valueResponse?.data);
            setValues(list);
        } catch (fetchError) {
            console.error(fetchError);
            setError("Không thể tải danh sách lựa chọn.");
        } finally {
            setLoading(false);
        }
    }, [optionId]);

    useEffect(() => {
        fetchValues();
    }, [fetchValues]);

    const resetForm = () => {
        setFormLabel("");
        setFormAvailability(true);
        setFormError("");
        setEditingValueId(null);
        setFormMode("create");
    };

    const openCreateModal = () => {
        resetForm();
        setShowFormModal(true);
    };

    const openEditModal = (value: any) => {
        const valueId = getOptionValueId(value);
        setEditingValueId(valueId ?? null);
        setFormMode("edit");
        setFormLabel(getOptionValueLabel(value));
        setFormAvailability(getOptionValueAvailability(value));
        setFormError("");
        setShowFormModal(true);
    };

    const closeFormModal = () => {
        setShowFormModal(false);
        resetForm();
    };

    const handleSubmitForm = async () => {
        if (!optionId) {
            setFormError("Không xác định được tùy chọn.");
            return;
        }

        const trimmed = formLabel.trim();
        if (!trimmed) {
            setFormError("Vui lòng nhập tên lựa chọn.");
            return;
        }

        try {
            if (formMode === "edit") {
                if (!editingValueId) {
                    setFormError("Không xác định được lựa chọn cần cập nhật.");
                    return;
                }

                await updateMenuOptionValue(optionId, editingValueId, {
                    label: trimmed,
                    is_available: formAvailability,
                });
                dispatch(
                    showAlertWithTimeout(
                        "success",
                        "Thành công",
                        "Cập nhật lựa chọn thành công!"
                    )
                );
            } else {
                await createMenuOptionValue(optionId, {
                    label: trimmed,
                    is_available: formAvailability,
                });
                dispatch(
                    showAlertWithTimeout(
                        "success",
                        "Thành công",
                        "Thêm lựa chọn thành công!"
                    )
                );
            }

            closeFormModal();
            fetchValues();
        } catch (submitError) {
            console.error(submitError);
            setFormError("Không thể lưu lựa chọn. Vui lòng thử lại.");
        }
    };

    const openDeleteConfirmation = (value: any) => {
        const valueId = getOptionValueId(value);
        if (!valueId) {
            dispatch(
                showAlertWithTimeout(
                    "warning",
                    "Cảnh báo",
                    "Không xác định được lựa chọn để xóa."
                )
            );
            return;
        }
        setDeleteTargetId(valueId);
        setDeleteTargetLabel(getOptionValueLabel(value));
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteTargetId(null);
        setDeleteTargetLabel("");
    };

    const handleConfirmDelete = async () => {
        if (!optionId || !deleteTargetId) {
            return;
        }

        try {
            await deleteMenuOptionValue(optionId, deleteTargetId);
            dispatch(
                showAlertWithTimeout(
                    "success",
                    "Thành công",
                    "Xóa lựa chọn thành công!"
                )
            );
            closeDeleteModal();
            fetchValues();
        } catch (deleteError) {
            console.error(deleteError);
            dispatch(
                showAlertWithTimeout(
                    "danger",
                    "Thất bại",
                    "Xóa lựa chọn thất bại. Vui lòng thử lại."
                )
            );
        }
    };

    const renderTableBody = () => {
        if (loading) {
            return (
                <tr>
                    <td colSpan={4} className="text-center py-3">
                        Đang tải danh sách lựa chọn...
                    </td>
                </tr>
            );
        }

        if (values.length === 0) {
            return (
                <tr>
                    <td colSpan={4} className="text-center py-3">
                        {error ?? "Chưa có lựa chọn nào."}
                    </td>
                </tr>
            );
        }

        return values.map((value, index) => {
            const valueId = getOptionValueId(value);
            const isAvailable = getOptionValueAvailability(value);
            const statusClass = isAvailable ? "badge bg-success" : "badge bg-secondary";
            const statusLabel = isAvailable ? "Đang bán" : "Ngưng bán";

            return (
                <tr key={valueId ?? `value-${index}`}>
                    <td>
                        <span className="fw-medium d-block">{getOptionValueLabel(value)}</span>
                    </td>
                    <td className="text-center">
                        <span className={statusClass}>{statusLabel}</span>
                    </td>
                    <td className="text-end">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm text-white me-1"
                            onClick={() => openEditModal(value)}
                        >
                            <i className="mdi mdi-lead-pencil"></i>
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger btn-sm text-white"
                            onClick={() => openDeleteConfirmation(value)}
                        >
                            <i className="mdi mdi-delete"></i>
                        </button>
                    </td>
                </tr>
            );
        });
    };

    return (
        <React.Fragment>
            <ConfirmModal
                isOpen={showDeleteModal}
                typeConfirm="danger"
                title="Xác nhận xóa"
                content={
                    deleteTargetLabel
                        ? `Bạn có chắc chắn muốn xóa lựa chọn "${deleteTargetLabel}"?`
                        : "Bạn có chắc chắn muốn xóa lựa chọn này không?"
                }
                onConfirm={() => {
                    setShowDeleteModal(false);
                    handleConfirmDelete();
                }}
                onCancel={closeDeleteModal}
            />

            <Modal size="sm" isOpen={showFormModal} toggle={closeFormModal} centered>
                <ModalBody>
                    <h6 className="fs-15 mb-3">
                        {formMode === "edit" ? "Chỉnh sửa lựa chọn" : "Thêm lựa chọn mới"}
                    </h6>
                    <div className="mb-3">
                        <label className="form-label">Tên lựa chọn</label>
                        <input
                            className={`form-control ${formError ? "is-invalid" : ""}`}
                            type="text"
                            placeholder="VD: S, M, L, 50% đá, 100% đá, ..."
                            value={formLabel}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setFormLabel(event.target.value);
                                if (formError) {
                                    setFormError("");
                                }
                            }}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Trạng thái</label>
                        <select
                            className="form-select"
                            value={formAvailability ? "true" : "false"}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                setFormAvailability(event.target.value === "true");
                            }}
                        >
                            <option value="true">Đang bán</option>
                            <option value="false">Ngưng bán</option>
                        </select>
                    </div>
                    {formError && <div className="alert alert-danger">{formError}</div>}
                </ModalBody>
                <div className="modal-footer">
                    <Button color="secondary" onClick={closeFormModal}>
                        Hủy
                    </Button>
                    <Button color="primary" onClick={handleSubmitForm}>
                        Lưu
                    </Button>
                </div>
            </Modal>

            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <div className="back-button me-2">
                        <Link to="/menu/options">
                            <i className="mdi mdi-arrow-left"></i>
                        </Link>
                    </div>

                    <div className="page-heading">
                        <h6 className="mb-0">{optionName}</h6>
                    </div>

                    <div className="d-flex align-items-center">
                        <div className="filter-option" onClick={openCreateModal} role="button">
                            <i className="mdi mdi-plus"></i>
                        </div>
                        <HeaderSidebarToggle className="ms-2" />
                    </div>
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="cart-wrapper-area py-3">
                        <div className="card">
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table mb-0">
                                        <thead>
                                            <tr>
                                                <th>Lựa chọn</th>
                                                <th className="text-center">Trạng thái</th>
                                                <th className="text-end">Thao tác</th>
                                            </tr>
                                        </thead>
                                        <tbody>{renderTableBody()}</tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default MenuOptionValuesPage;
